import React, { FunctionComponent, useState } from 'react'
import 'c3/c3.css'
import { HostData } from '../Customer/Data'
import { BriefDonut } from './BriefDonut'
import { BarSeriesChart, SeriesPoint } from '../Visualizations/BarSeriesChart'
import { KeyInsights } from './KeyInsights'
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal'
import {
  OfficePrinterIcon,
  FeaturesUnderutilizedIcon,
  MonoVolumeIcon,
  ColorVolumeIcon,
  FleetPerformanceSectionIcon,
  HighlightsSectionIcon,
  SupportServiceSectionIcon,
  FeatureUtilizationSectionIcon,
  InfoIcon,
  NoData,
} from '../Icons/Icons'
import { BriefLoading } from './BriefLoading'
import { BriefShell, BriefContentContext } from './BriefShell'
import { SectionWithNote } from './SectionWithNote'
import { DevicesSection, PrintQueuesSection } from './DeviceSections'
import { Comparison, CompositionValue, CountValue, DevicesData, Note, PrintQueuesData } from './BriefSectionTypes'
import { findNote, formatMoney, formatThousands } from './briefHelpers'
import { NotesBubble, renderExpandableExplanation } from './PinnedNotesCard'
import styles from './BriefShell.module.css'
import healthStyles from './HealthCheckBrief.module.css'
import { useBriefData } from './useBriefData'
import { useCustomerProfile } from './useCustomerProfile'

interface Props {
  teamID: string
  host: HostData
}

interface TileProps {
  tile: ViewTile
  valueSize?: TileValueSize
  calculationTopic?: CalculationTopic
  onOpenCalculation?: (topic: CalculationTopic) => void
}

// --- Domain payload ---

interface RatioValue {
  value: number
  comparison?: Comparison
  deviceRef?: string
}

interface SeriesValue {
  points: SeriesPoint[]
  average: number
}

interface PrintVolumeChannel {
  current: number
  average: number
  comparison?: Comparison
}

interface PrintVolumeValue {
  total: PrintVolumeChannel
  bw: PrintVolumeChannel
  color: PrintVolumeChannel
}

type Metric =
  | { id: string; kind: 'composition'; composition: CompositionValue }
  | { id: string; kind: 'count'; count: CountValue }
  | { id: string; kind: 'ratio'; ratio: RatioValue }
  | { id: string; kind: 'series'; series: SeriesValue }
  | { id: string; kind: 'printVolume'; printVolume: PrintVolumeValue }

interface Section {
  id: string
  title: string
  metrics: Metric[]
  featureUtilization?: FeatureUtilizationData
  printQueues?: PrintQueuesData
  devices?: DevicesData
  notes?: Note[]
}

type Feature = {
  id: string
  active: boolean
}

type FeatureCategory = {
  id: string
  features: Feature[]
}

interface FeatureUtilizationData {
  categories: FeatureCategory[]
}

interface HealthCheckData {
  sections: Section[]
}

interface HealthCheckBriefing {
  type: string
  data: HealthCheckData
  insights: string[]
  createdAt: string
}

type CalculationTopic = 'fleet' | 'risk'

const CATEGORY_LABELS: Record<string, string> = {
  'audit-logging': 'Audit logging',
  'security-control': 'Security & control',
  'mobility-access': 'Mobility & access',
  'automation-workflow': 'Automation & workflow',
}

const FEATURE_LABELS: Record<string, string> = {
  'job-logging': 'Job logging',
  'print-tracking': 'Print tracking',
  'secure-print-release': 'Secure Print Release',
  'find-me-printing': 'Find-Me printing',
  watermarking: 'Watermarking and digital signatures',
  'mobility-print': 'Mobility Print',
  'web-print': 'Web Print',
  'integrated-scanning': 'Integrated Scanning',
}

/*
 * Demo-only: these overall benchmark averages are deliberately hardcoded from
 * the supplied reference data, not calculated from live customer usage.
 */
const FEATURE_BENCHMARK_AVERAGES: Record<string, string> = {
  watermarking: '1%',
  'mobility-print': '47%',
  'web-print': '21%',
  'integrated-scanning': '64%',
}

const EMPTY_VALUE = '—'
const NO_DATA_SUBTEXT = 'No data available'

const METRIC_LABELS: Record<string, string> = {
  'fleet-brand': 'Fleet makeup — brand',
  'features-underutilized': 'Features underutilized',
  'page-volume-mono': 'B&W pages',
  'page-volume-color': 'Color pages',
  'print-volume': 'Print volume',
  'volume-trend': 'Print volume',
  'opened-tickets': 'Tickets opened in the last month',
}

interface SeriesTileStyle {
  color: string
  unitLabel: string
}

const SERIES_TILE_STYLES: Record<string, SeriesTileStyle> = {
  // a soft blue: reads as live data (grey looked disabled) without competing with the coloured charts
  'volume-trend': { color: '#9EBDF6', unitLabel: 'pages' },
}
const DEFAULT_SERIES_TILE_STYLE: SeriesTileStyle = { color: '#9EBDF6', unitLabel: 'items' }

const formatPercent = (fraction: number): string => `${(fraction * 100).toFixed(1)}%`

const formatCurrency = (val: number | undefined, currencyCode: string | undefined): string => {
  if (val === undefined) return ''
  const num = Number(val)
  if (Number.isNaN(num)) return ''
  return formatMoney(num, currencyCode)
}

const formatMagnitudePercent = (fraction: number): string =>
  `${(Math.abs(fraction) * 100).toFixed(1).replace(/\.0$/, '')}%`

const trendOf = (percentChange: number): 'up' | 'down' | '' => {
  if (percentChange > 0) return 'up'
  if (percentChange < 0) return 'down'
  return ''
}

const trendArrow = (trend?: string): React.ReactNode => {
  if (trend === 'up')
    return (
      <span>
        <i className={`icon twelve ${styles.trendUp}`}>
          <svg viewBox='0 0 24 24'>
            <path
              d='m12 23.247 0 -22.5'
              fill='none'
              stroke='currentColor'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='3'
            />
            <path
              d='M8.25 4.497 12 0.747l3.75 3.75'
              fill='none'
              stroke='currentColor'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='3'
            />
          </svg>
        </i>
      </span>
    )
  if (trend === 'down')
    return (
      <span>
        <i className={`icon twelve ${styles.trendDown}`}>
          <svg viewBox='0 0 24 24'>
            <path
              d='m12 23.247 0 -22.5'
              fill='none'
              stroke='currentColor'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='3'
            />
            <path
              d='m8.25 19.497 3.75 3.75 3.75 -3.75'
              fill='none'
              stroke='currentColor'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='3'
            />
          </svg>
        </i>
      </span>
    )
  return null
}

interface ViewSegment {
  label: string
  count: number
}

interface ViewTile {
  key: string
  label: string
  value: string
  icon?: React.ReactNode
  unit?: string
  subtext?: string
  trend?: 'up' | 'down' | ''
  segments?: ViewSegment[]
}

type TileValueSize = 'sm' | 'md'

const metricView = (m: Exclude<Metric, { kind: 'series' | 'printVolume' }>): ViewTile => {
  const id = m.id
  const label = METRIC_LABELS[id] ?? id
  const icon = renderMetricIcon(m.id)

  if (m.kind === 'composition') {
    const segments = m.composition.segments.map(s => ({
      label: s.label,
      count: s.count,
    }))
    return { key: m.id, label, value: formatThousands(m.composition.total), icon, subtext: 'devices', segments }
  }

  if (m.kind === 'count') {
    const c = m.count
    if (!c.hasData) {
      return { key: m.id, label, value: EMPTY_VALUE, icon, subtext: NO_DATA_SUBTEXT }
    }
    return { key: m.id, label, value: formatThousands(c.value), icon }
  }

  if (m.kind === 'ratio') {
    const r = m.ratio
    return { key: m.id, label, value: formatPercent(r.value), icon, trend: trendOf(r.comparison?.percentChange ?? 0) }
  }

  return { key: id, label, value: '—', icon }
}

interface OverviewNotesCardProps {
  title: string
  note: Note
  expanded: boolean
  onToggle: () => void
  hideMetrics?: boolean
  onOpenCalculation?: (topic: CalculationTopic) => void
}

const OverviewNotesCard: FunctionComponent<OverviewNotesCardProps> = ({
  title,
  note,
  expanded,
  onToggle,
  hideMetrics = false,
  onOpenCalculation,
}) => (
  <div data-testid='overview-notes-card'>
    <NotesBubble title={title}>
      {!hideMetrics && (
        <div className={styles.notesDetails}>
          <dl className='no-margin-bottom'>
            <div className={styles.notesPair}>
              <dt>
                Estimated fleet value{' '}
                <a
                  className='has-tip'
                  href='#how-calculated'
                  onClick={e => {
                    e.preventDefault()
                    onOpenCalculation?.('fleet')
                  }}
                >
                  <span className='tip center'>View calculation details</span>
                  <i className='icon twelve'>
                    <InfoIcon />
                  </i>
                </a>
              </dt>
              <dd>
                <span className='num'>
                  {formatCurrency(note.renewalRisk?.estimatedFleetValue, note.renewalRisk?.currencyCode)}
                </span>
              </dd>
            </div>
            <div className={styles.notesPair}>
              <dt>
                Renewal risk{' '}
                <a
                  className='has-tip'
                  href='#how-calculated'
                  onClick={e => {
                    e.preventDefault()
                    onOpenCalculation?.('risk')
                  }}
                >
                  <span className='tip center'>View calculation details</span>
                  <i className='icon twelve'>
                    <InfoIcon />
                  </i>
                </a>
              </dt>
              <dd>
                <div className={styles.notesFlag}>{note.renewalRisk?.risk ?? ''}</div>
              </dd>
            </div>
          </dl>
        </div>
      )}
      {renderExpandableExplanation(note.explanation, expanded, onToggle, 'notes-explanation')}
    </NotesBubble>
  </div>
)

// the legend sits to the right of the donut and wraps into extra columns once it runs out of height,
// so the tile widens per column and the donut keeps its size (about 10 legend rows fit beside it)
const LEGEND_ROWS = 10
const legendColumns = (segmentCount: number): number => Math.min(3, Math.max(1, Math.ceil(segmentCount / LEGEND_ROWS)))

const MakeupTile: FunctionComponent<{ tile: ViewTile }> = ({ tile }) => (
  <div className={healthStyles.makeupTile} data-legend-cols={legendColumns(tile.segments?.length ?? 0)}>
    <div className={healthStyles.chartContainer}>
      <BriefDonut
        id={`${tile.key}-donut`}
        title={tile.value}
        data={(tile.segments ?? []).map(segment => [segment.label, segment.count] as [string, number])}
      />
    </div>
    <header className={healthStyles.makeupHeader}>
      <h5>{tile.label}</h5>
    </header>
  </div>
)

const renderMetricIcon = (key: string): React.ReactNode => {
  switch (key) {
    case 'features-underutilized':
      return <FeaturesUnderutilizedIcon />
    case 'opened-tickets':
      return <SupportServiceSectionIcon />
    case 'page-volume-mono':
      return <MonoVolumeIcon />
    case 'page-volume-color':
      return <ColorVolumeIcon />
    default:
      return (
        <svg
          viewBox='0 0 24 24'
          fill='none'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <path d='M3 3v18h18' />
          <path d='m19 9-5 5-4-4-3 3' />
        </svg>
      )
  }
}

const Tile: FunctionComponent<TileProps> = ({ tile, valueSize = 'md', calculationTopic, onOpenCalculation }) => {
  const isNoData = tile.subtext === NO_DATA_SUBTEXT

  return (
    <div className={`surface ${styles.metricSurface}`}>
      <div className='flex flex-nowrap'>
        {tile.icon && <i className='icon thirtytwo flex-noshrink push'>{tile.icon}</i>}
        <div>
          <div className={styles.tileValue} data-value-size={valueSize}>
            {tile.value}
            {trendArrow(tile.trend)}
            {isNoData && <span className={`${styles.comingSoon} ${styles.syncingFlag}`}>{tile.subtext}</span>}
          </div>
          <h5>
            {tile.label}{' '}
            {calculationTopic && onOpenCalculation && (
              <a
                className='has-tip'
                href='#how-calculated'
                onClick={e => {
                  e.preventDefault()
                  onOpenCalculation(calculationTopic)
                }}
              >
                <span className='tip center'>View calculation details</span>
                <i className='icon twelve'>
                  <InfoIcon />
                </i>
              </a>
            )}
          </h5>
          {tile.subtext && !isNoData && (
            <small className={`blk subdued no-margin-bottom ${styles.metricSubtext}`}>{tile.subtext}</small>
          )}
        </div>
      </div>
    </div>
  )
}

interface PrintVolumeBreakdownRow {
  key: string
  label: string
  value: string
  swatch: 'bw' | 'color'
}

interface PrintVolumeTileView {
  key: string
  label: string
  value: string
  icon?: React.ReactNode
  unit: string
  delta?: { magnitude: string; trend: 'up' | 'down' | ''; caption: string }
  breakdown: PrintVolumeBreakdownRow[]
}

const printVolumeTileOf = (m: Extract<Metric, { kind: 'printVolume' }>): PrintVolumeTileView => {
  const pv = m.printVolume
  const change = pv.total.comparison?.percentChange
  return {
    key: m.id,
    label: METRIC_LABELS[m.id] ?? m.id,
    icon: renderMetricIcon(m.id),
    value: formatThousands(pv.total.current),
    unit: 'pages last month',
    delta:
      change === undefined
        ? undefined
        : { magnitude: formatMagnitudePercent(change), trend: trendOf(change), caption: 'vs 12-month average' },
    breakdown: [
      { key: 'bw', label: 'B&W', value: formatThousands(pv.bw.current), swatch: 'bw' },
      { key: 'color', label: 'color', value: formatThousands(pv.color.current), swatch: 'color' },
    ],
  }
}

const deltaArrow = (trend: 'up' | 'down' | ''): string => (trend === 'up' ? '↑ ' : trend === 'down' ? '↓ ' : '')

const deltaClass = (trend: 'up' | 'down' | ''): string =>
  trend === 'up' ? styles.deltaUp : trend === 'down' ? styles.deltaDown : 'subdued'

const PrintVolumeTile: FunctionComponent<{ tile: PrintVolumeTileView }> = ({ tile }) => (
  <div className={`surface ${styles.metricSurface}`}>
    <div className='flex flex-nowrap'>
      {tile.icon && <i className='icon thirtytwo flex-noshrink push'>{tile.icon}</i>}
      <div>
        <div className='med'>
          <div className='flex flex-nowrap flex-end'>
            {tile.value}
            <span className={`subdued ${styles.printVolumeUnit}`}>{tile.unit}</span>
          </div>
        </div>
        <h5>{tile.label}</h5>
        {tile.delta && (
          <small className={`blk ${styles.printVolumeDelta}`}>
            <span className={deltaClass(tile.delta.trend)}>
              {deltaArrow(tile.delta.trend)}
              {tile.delta.magnitude}
            </span>{' '}
            <span className='subdued'>{tile.delta.caption}</span>
          </small>
        )}
        <div className={styles.printVolumeBreakdown}>
          {tile.breakdown.map(row => (
            <span key={row.key} className={styles.breakdownRow}>
              <span
                className={`${styles.swatch} ${row.swatch === 'bw' ? styles.swatchBW : styles.swatchColor}`}
                aria-hidden='true'
              />
              <span className='subdued'>
                {row.value} {row.label}
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  </div>
)

interface SeriesTile {
  key: string
  label: string
  points: SeriesPoint[]
  average: number
  color: string
  unitLabel: string
}

const seriesTileOf = (m: Extract<Metric, { kind: 'series' }>): SeriesTile => {
  const style = SERIES_TILE_STYLES[m.id] ?? DEFAULT_SERIES_TILE_STYLE
  return {
    key: m.id,
    label: METRIC_LABELS[m.id] ?? m.id,
    points: m.series.points,
    average: m.series.average,
    color: style.color,
    unitLabel: style.unitLabel,
  }
}

const averageCaption = (tile: SeriesTile): string | null => {
  const monthsWithData = tile.points.filter(p => p.hasData).length
  if (monthsWithData === 0) return null
  const monthWord = monthsWithData === 1 ? 'month' : 'months'
  return `Average ${formatThousands(tile.average)} ${tile.unitLabel}/month over ${monthsWithData} ${monthWord}`
}

const SeriesTileCard: FunctionComponent<{ tile: SeriesTile }> = ({ tile }) => {
  const caption = averageCaption(tile)
  const hasNoData = !tile.points.some(p => p.hasData)

  return (
    <div className={healthStyles.seriesTileCard}>
      <header>
        <h4 className='btm'>{tile.label}</h4>
      </header>
      <div className={healthStyles.chartWrapper}>
        <BarSeriesChart
          id={`${tile.key}-chart`}
          style={{ height: '200px' }}
          points={tile.points}
          average={tile.average}
          color={tile.color}
          unitLabel={tile.unitLabel}
          valueFormat={formatThousands}
        />
        {hasNoData && (
          <div className={healthStyles.noDataOverlay}>
            <i className='icon twentyfour dip'>
              <NoData />
            </i>
            <h5>No data available</h5>
            <p>We're waiting on activity or still processing.</p>
          </div>
        )}
      </div>
      {caption && (
        <small
          className={`blk subdued no-margin-bottom ${styles.seriesAverageCaption}`}
          data-testid={`${tile.key}-average`}
        >
          {caption}
        </small>
      )}
    </div>
  )
}

const FeatureUtilization: FunctionComponent<{ categories: FeatureCategory[] }> = ({ categories }) => {
  if (categories.length === 0) {
    return null
  }

  const categoryLabel = (id: string) => CATEGORY_LABELS[id] ?? id
  const featureLabel = (id: string) => FEATURE_LABELS[id] ?? id

  const longestCategory = categories.reduce((longest, category) => {
    const label = categoryLabel(category.id)
    return label.length > longest.length ? label : longest
  }, '')

  const icon = renderSectionIcon('feature utilization')

  return (
    <div className={styles.sectionWrapper}>
      <header className={styles.sectionHeader}>
        {icon && (
          <figure className='angular'>
            <i className='icon thirtytwo flex-noshrink'>{icon}</i>
          </figure>
        )}
        <h3 className={styles.sectionTitle}>Feature utilization</h3>
      </header>
      <div className={`${styles.tableContainer} ${styles.sectionContainer}`}>
        <table className={styles.table} aria-label='Feature utilization'>
          <thead>
            <tr>
              <th>Category</th>
              <th>Name</th>
              <th>Status (Actual)</th>
              <th>Benchmark (Average)</th>
            </tr>
          </thead>
          <tbody>
            {categories.map(category => (
              <React.Fragment key={category.id}>
                <tr>
                  <th colSpan={4} className={styles.catRow}>
                    {categoryLabel(category.id)}
                  </th>
                </tr>
                {category.features.map(feature => (
                  <tr key={feature.id}>
                    <td>
                      <span className={styles.hiddenRow}>{longestCategory}</span>
                    </td>
                    <td>{featureLabel(feature.id)}</td>
                    <td className={feature.active ? undefined : styles.rowWarn}>{feature.active ? 'Yes' : 'No'}</td>
                    <td>{FEATURE_BENCHMARK_AVERAGES[feature.id] ?? '-'}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const renderSectionIcon = (title: string): React.ReactNode | null => {
  const normalizedTitle = title.toLowerCase().trim()

  if (normalizedTitle.includes('no-icon') || normalizedTitle === 'overview') {
    return null
  }

  if (normalizedTitle.includes('highlight')) {
    return <HighlightsSectionIcon />
  }

  // print queues and devices with embedded apps share the printer icon
  if (normalizedTitle.includes('queue') || normalizedTitle.includes('device') || normalizedTitle.includes('embedded')) {
    return <OfficePrinterIcon />
  }

  if (
    normalizedTitle.includes('usage') ||
    normalizedTitle.includes('performance') ||
    normalizedTitle.includes('fleet')
  ) {
    return <FleetPerformanceSectionIcon />
  }

  if (
    normalizedTitle.includes('support') ||
    normalizedTitle.includes('ticket') ||
    normalizedTitle.includes('service')
  ) {
    return <SupportServiceSectionIcon />
  }

  if (
    normalizedTitle.includes('feature') ||
    normalizedTitle.includes('utilization') ||
    normalizedTitle.includes('module')
  ) {
    return <FeatureUtilizationSectionIcon />
  }

  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <circle cx='12' cy='12' r='9' />
      <line x1='12' y1='8' x2='12' y2='12' />
      <line x1='12' y1='16' x2='12.01' y2='16' />
    </svg>
  )
}

const SectionBlock: FunctionComponent<{ section: Section }> = ({ section }) => {
  const makeupTiles = section.metrics
    .filter((m): m is Extract<Metric, { kind: 'composition' }> => m.kind === 'composition')
    .map(metricView)
  const gridMetrics = section.metrics.filter(
    (m): m is Extract<Metric, { kind: 'count' | 'ratio' | 'printVolume' }> =>
      m.kind === 'count' || m.kind === 'ratio' || m.kind === 'printVolume'
  )
  const seriesTiles = section.metrics
    .filter((m): m is Extract<Metric, { kind: 'series' }> => m.kind === 'series')
    .map(seriesTileOf)

  if (makeupTiles.length === 0 && gridMetrics.length === 0 && seriesTiles.length === 0) {
    return null
  }

  const icon = renderSectionIcon(section.title)

  return (
    <section className={styles.section}>
      <header className={styles.sectionHeader}>
        {icon && (
          <figure className='angular'>
            <i className='icon thirtytwo flex-noshrink'>{icon}</i>
          </figure>
        )}
        <h3 className={styles.sectionTitle}>{section.title}</h3>
      </header>
      {(makeupTiles.length > 0 || gridMetrics.length > 0) && (
        <div className={styles.highlightsRow}>
          {makeupTiles.length > 0 && (
            <div className={healthStyles.makeupGrid}>
              {makeupTiles.map(tile => (
                <MakeupTile key={tile.key} tile={tile} />
              ))}
            </div>
          )}
          {gridMetrics.length > 0 && (
            <div className={styles.metricStack}>
              {gridMetrics.map(m =>
                m.kind === 'printVolume' ? (
                  <PrintVolumeTile key={m.id} tile={printVolumeTileOf(m)} />
                ) : (
                  <Tile key={m.id} tile={metricView(m)} />
                )
              )}
            </div>
          )}
        </div>
      )}
      {seriesTiles.length > 0 && (
        <div className={styles.seriesSection}>
          {seriesTiles.map(tile => (
            <SeriesTileCard key={tile.key} tile={tile} />
          ))}
        </div>
      )}
    </section>
  )
}

export const HealthCheckBrief: FunctionComponent<Props> = ({ teamID, host }) => {
  const { briefing, loading, error } = useBriefData<HealthCheckBriefing>({
    teamID,
    customerID: host.id,
    briefType: 'health-check',
    errorFallback: 'An error occurred while generating the health check',
  })
  const { profile, loading: profileLoading } = useCustomerProfile(teamID, host.id)
  // the first note on the brief (beside the overview) opens expanded
  const [showMoreOverviewNotes, setShowMoreOverviewNotes] = useState(true)
  const [calculationTopic, setCalculationTopic] = useState<CalculationTopic | null>(null)

  if (loading || profileLoading) {
    return (
      <div className={`empty loading ${styles.loadingState}`} data-testid='loading-dots'>
        <div className={styles.loadingMessage}>
          <BriefLoading />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`alert error ${styles.errorState}`} data-testid='health-check-error'>
        <div>Failed to generate health check: {error}</div>
      </div>
    )
  }

  const sections = briefing?.data?.sections ?? []
  const reportingSections = sections.filter(
    section => section.id === 'highlights' || section.id === 'usage-performance'
  )
  const additionalSections = sections.filter(
    section => section.id !== 'highlights' && section.id !== 'usage-performance'
  )
  const renewalRiskNote = findNote(sections, 'highlights', 'renewal-risk')
  const renewalRisk = renewalRiskNote?.renewalRisk
  const pinnedFeatureNote = findNote(sections, 'feature-utilization', 'feature-utilization')
  const pinnedDevicesNote = findNote(sections, 'devices', 'devices')
  const pinnedPrintQueuesNote = findNote(sections, 'print-queues', 'print-queues')
  const pinnedVolumeTrendNote = findNote(sections, 'usage-performance', 'volume-trend')

  const renderBriefSection = (
    section: Section,
    { activeSideNotes, onNoteLayoutChange }: BriefContentContext
  ): React.ReactNode => {
    switch (section.id) {
      case 'usage-performance': {
        const hasVolumeTrendSeries = section.metrics.some(
          metric => metric.id === 'volume-trend' && metric.kind === 'series'
        )

        if (!hasVolumeTrendSeries) {
          return <SectionBlock key={section.id} section={section} />
        }

        return (
          <SectionWithNote
            key={section.id}
            noteTitle={section.title}
            activeSideNotes={activeSideNotes}
            note={pinnedVolumeTrendNote}
            segmentTestId='usage-performance-segment'
            noteTestId='volume-trend-note'
            onNoteLayoutChange={onNoteLayoutChange}
          >
            <SectionBlock section={section} />
          </SectionWithNote>
        )
      }
      case 'feature-utilization': {
        const categories = section.featureUtilization?.categories ?? []
        if (categories.length === 0) {
          return null
        }
        return (
          <SectionWithNote
            key={section.id}
            // the backend sends this section without a title, so name its notes here
            noteTitle={section.title || 'Feature utilization'}
            activeSideNotes={activeSideNotes}
            note={pinnedFeatureNote}
            segmentTestId='feature-utilization-segment'
            noteTestId='feature-utilization-note'
            onNoteLayoutChange={onNoteLayoutChange}
          >
            <FeatureUtilization categories={categories} />
          </SectionWithNote>
        )
      }
      case 'print-queues': {
        const queues = section.printQueues?.queues ?? []
        if (queues.length === 0) {
          return null
        }
        return (
          <SectionWithNote
            key={section.id}
            noteTitle={section.title}
            activeSideNotes={activeSideNotes}
            note={pinnedPrintQueuesNote}
            segmentTestId='print-queues-segment'
            noteTestId='print-queues-note'
            onNoteLayoutChange={onNoteLayoutChange}
          >
            <PrintQueuesSection
              queues={queues}
              renderTile={(tile, valueSize) => <Tile tile={tile} valueSize={valueSize} />}
              renderSectionIcon={renderSectionIcon}
            />
          </SectionWithNote>
        )
      }
      case 'devices': {
        const devices = section.devices?.devices ?? []
        if (devices.length <= 1) {
          return null
        }
        return (
          <SectionWithNote
            key={section.id}
            noteTitle={section.title}
            activeSideNotes={activeSideNotes}
            note={pinnedDevicesNote}
            segmentTestId='devices-segment'
            noteTestId='devices-note'
            onNoteLayoutChange={onNoteLayoutChange}
          >
            <DevicesSection
              devices={devices}
              renderTile={(tile, valueSize) => <Tile tile={tile} valueSize={valueSize} />}
              renderSectionIcon={renderSectionIcon}
            />
          </SectionWithNote>
        )
      }
      default:
        return <SectionBlock key={section.id} section={section} />
    }
  }

  return (
    <>
      <BriefShell
        teamID={teamID}
        host={host}
        briefType='health-check'
        title='Health check'
        generatedAt={briefing?.createdAt}
        industry={profile?.industry}
        suggestionChips={[
          'Position this for the customer',
          'Draft an email to the customer',
          'Talking points for my meeting',
        ]}
        segmentTestId='overview-notes-segment'
        layoutContent={briefing}
        verticalPods={
          <div className={styles.metricGrid}>
            <Tile
              tile={{
                key: 'fleet-value',
                label: 'Estimated fleet value',
                value: formatCurrency(renewalRisk?.estimatedFleetValue, renewalRisk?.currencyCode) || '—',
                icon: <OfficePrinterIcon />,
              }}
              valueSize='md'
              calculationTopic='fleet'
              onOpenCalculation={topic => setCalculationTopic(topic)}
            />
            <Tile
              tile={{
                key: 'fleet-risk',
                label: 'Renewal risk',
                value: renewalRisk?.risk ?? '—',
                icon: <FeatureUtilizationSectionIcon />,
              }}
              valueSize='md'
              calculationTopic='risk'
              onOpenCalculation={topic => setCalculationTopic(topic)}
            />
          </div>
        }
        renderOverview={context => {
          const { activeSideNotes, onNoteLayoutChange } = context
          return (
            <>
              {reportingSections.map(section => renderBriefSection(section, context))}
              <div className={styles.screenOnly}>
                <KeyInsights insights={briefing?.insights ?? []} createdAt={briefing?.createdAt} />
              </div>
              {activeSideNotes && renewalRisk && (
                <div className={styles.absoluteNote} data-guide='notes'>
                  <OverviewNotesCard
                    title={sections.find(section => section.id === 'highlights')?.title || 'Highlights'}
                    note={renewalRiskNote}
                    expanded={showMoreOverviewNotes}
                    onToggle={() => {
                      setShowMoreOverviewNotes(previous => !previous)
                      onNoteLayoutChange()
                    }}
                    hideMetrics={context.podsVisible}
                    onOpenCalculation={topic => setCalculationTopic(topic)}
                  />
                </div>
              )}
            </>
          )
        }}
      >
        {context => additionalSections.map(section => renderBriefSection(section, context))}
      </BriefShell>
      {/* Calculation Methodology Modal */}
      {calculationTopic !== null && (
        <ConfirmationModal
          title={calculationTopic === 'fleet' ? 'How estimated fleet value is calculated' : 'How risk is calculated'}
          message={
            calculationTopic === 'fleet'
              ? 'Estimated fleet value = (devices × average value per device) + (average B&W pages × cost per B&W page) + (average color pages × cost per color page). The value per device and page costs come from the Intel tab.'
              : 'The risk score is calculated by cross-referencing support expiration timelines, inactive security features, and volume drops against historical customer baselines. Higher risk flags highlight accounts that may require proactive software optimization or renewal discussions.'
          }
          cannotUndoWarning={false}
          showCancel={false}
          yesLabel='OK'
          onClose={() => setCalculationTopic(null)}
          onYes={() => setCalculationTopic(null)}
        />
      )}
    </>
  )
}
